import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { colors, fontSize, fontWeight } from '../../style/tokens';
import { gaugeBandIndex, gaugeBandLabel } from './ceIndicatorGroups';

interface CeVitalGaugeRowProps {
  label: string;
  value: string;
  /**
   * bands=2 (vital parameters): score 0–100 where 100 = most normal → marker left (green).
   * bands=3 (stress / risk):    score 0–100 where 0 = low/good → marker left (green), 100 = high/bad → marker right (orange).
   */
  score: number;
  bands?: 2 | 3;
  hideValue?: boolean;
  /**
   * The reading is licensed but this scan did not produce a value. Renders a grey
   * "not available for this scan" bar with no marker, mirroring the wellness
   * deck's unavailable state.
   */
  unavailable?: boolean;
  /**
   * The value is shown but was measured below the confidence gate. Appends a "*"
   * after the value; the report renders a matching low-confidence footnote.
   */
  lowConfidence?: boolean;
  /**
   * Range legend for numeric vitals: what "normal" (green) and "worth noting"
   * (yellow) mean for this reading. Ranges match this gauge's thresholds. When
   * omitted, no legend is shown. Only meaningful for 2-band gauges.
   */
  legend?: { normal: string; noting: string };
  /**
   * 3-band only: swap the low/high band labels so the green end reads "high"
   * and the orange end reads "low" (for indicators where high is the good
   * outcome, e.g. recovery ability). Arrow position is unaffected.
   */
  invertGaugeLabels?: boolean;
  /** When provided, the whole row becomes a button that opens the detail page. */
  onClick?: () => void;
}

const Wrapper = styled.div<{ $clickable?: boolean }>`
  padding: 14px 0 10px;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    @media (hover: hover) { &:hover { opacity: 0.75; } }
  `}
`;

const TopRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const Label = styled.span`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.bold};
  color: ${colors.text};
`;

const ValueText = styled.span`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.bold};
  color: ${colors.text};
`;

const LowConfMark = styled.span`
  color: #b26a00;
  margin-left: 2px;
`;

const GaugeOuter = styled.div`
  position: relative;
  padding: 7px 0;
`;

const GaugeTrack = styled.div`
  position: relative;
  display: flex;
  height: 32px;
  border-radius: 99px;
  overflow: hidden;
`;

const Seg = styled.div<{ $color: string }>`
  flex: 1;
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SegLabel = styled.span<{ $dark?: boolean }>`
  font-size: 11px;
  font-weight: ${fontWeight.semibold};
  font-style: italic;
  color: ${({ $dark }) => ($dark ? '#000' : '#fff')};
  user-select: none;
`;

const ArrowDown = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  left: ${({ $left }) => $left}%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid #333;
  pointer-events: none;
`;

const ArrowUp = styled.div<{ $left: number }>`
  position: absolute;
  bottom: 0;
  left: ${({ $left }) => $left}%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 7px solid #333;
  pointer-events: none;
`;

const GREEN  = 'rgb(148,193,30)';
const YELLOW = 'rgb(218,218,8)';
const ORANGE = 'rgb(230,120,0)';
const GREY   = '#eceff1';

// ── Range legend (numeric vitals) ───────────────────────────────────────────

// Two-column grid: the first column sizes to the WIDER pill's label, and both
// pills fill it (width:100%) — so the pills match width regardless of language.
const Legend = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 8px;
  row-gap: 4px;
  align-items: center;
  margin-top: 9px;
`;

// Coloured pill carrying the band name (matches the gauge segment colours).
const LegendPill = styled.span<{ $bg: string; $dark?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${({ $dark }) => ($dark ? '#000' : '#fff')};
  font-size: 10px;
  font-weight: ${fontWeight.semibold};
  white-space: nowrap;
`;

const LegendRange = styled.span`
  font-size: 11px;
  color: ${colors.muted};
`;

/**
 * Left offset (%) of the gauge arrow: the CENTRE of whichever band the reading
 * falls into (e.g. a "normal" reading → middle of the green band, "worth
 * noting" → middle of the yellow band), rather than a graded position within
 * the band. Which band is decided by the same 0–100 score used to colour the
 * track: for 2-band the score is inverted (100 = normal → left band); for
 * 3-band it is used directly (0 = low → left band).
 */
export function bandCenterLeft(score: number, bands: 2 | 3): number {
  return (gaugeBandIndex(score, bands) + 0.5) * (100 / bands);
}

const CeVitalGaugeRow: React.FC<CeVitalGaugeRowProps> = ({ label, value, score, bands = 2, hideValue = false, unavailable = false, lowConfidence = false, legend, invertGaugeLabels = false, onClick }) => {
  const intl = useIntl();

  const markerLeft = bandCenterLeft(score, bands);

  // Segment labels come from the shared band resolver, so the wording printed on
  // the bar and the band named in the JSON report are always the same.
  // For 3 bands: normally low(green) → high(orange); when inverted (high is
  // good) the ends swap so "high" sits on the green end.
  const greenLabelKey = gaugeBandLabel(0, bands, invertGaugeLabels).labelKey;
  const middleLabelKey = gaugeBandLabel(1, bands, invertGaugeLabels).labelKey;
  const orangeLabelKey = gaugeBandLabel(2, bands, invertGaugeLabels).labelKey;

  // A clickable row behaves as a button (pointer, keyboard-activable).
  const clickProps = onClick
    ? {
        $clickable: true,
        onClick,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
        },
      }
    : {};

  if (unavailable) {
    return (
      <Wrapper {...clickProps}>
        <TopRow>
          <Label>{label}</Label>
        </TopRow>
        <GaugeOuter>
          <GaugeTrack>
            <Seg $color={GREY}>
              <SegLabel $dark>{intl.formatMessage({ id: 'metric.ui.unavailable' })}</SegLabel>
            </Seg>
          </GaugeTrack>
        </GaugeOuter>
      </Wrapper>
    );
  }

  return (
    <Wrapper {...clickProps}>
      <TopRow>
        <Label>{label}</Label>
        {!hideValue && (
          <ValueText>
            {value}
            {lowConfidence && <LowConfMark aria-hidden="true">*</LowConfMark>}
          </ValueText>
        )}
      </TopRow>
      <GaugeOuter>
        <ArrowDown $left={markerLeft} />
        <GaugeTrack>
          <Seg $color={GREEN}>
            <SegLabel>
              {intl.formatMessage({ id: greenLabelKey })}
            </SegLabel>
          </Seg>
          <Seg $color={YELLOW}>
            <SegLabel $dark>
              {intl.formatMessage({ id: middleLabelKey })}
            </SegLabel>
          </Seg>
          {bands === 3 && (
            <Seg $color={ORANGE}>
              <SegLabel>
                {intl.formatMessage({ id: orangeLabelKey })}
              </SegLabel>
            </Seg>
          )}
        </GaugeTrack>
        <ArrowUp $left={markerLeft} />
      </GaugeOuter>
      {legend && (
        <Legend>
          <LegendPill $bg={GREEN}>{intl.formatMessage({ id: 'metric.gauge.normal' })}</LegendPill>
          <LegendRange>{legend.normal}</LegendRange>
          <LegendPill $bg={YELLOW} $dark>{intl.formatMessage({ id: 'metric.gauge.worthNoting' })}</LegendPill>
          <LegendRange>{legend.noting}</LegendRange>
        </Legend>
      )}
    </Wrapper>
  );
};

export default CeVitalGaugeRow;
